import React, { useState } from 'react'
import TableCell, { tableCellClasses } from '@mui/material/TableCell'
import { styled } from '@mui/material/styles'
import {
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  CircularProgress,
  TablePagination
} from '@mui/material'
import RefreshIcon from '@mui/icons-material/Refresh'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: '#EEEEEE',
    border: '1px solid #EAECF0',
    color: theme.palette.common.white,
    padding: '6px 6px'
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 12,
    padding: '6px 6px'
  }
}))

const StyledTableRow = styled(TableRow)(() => ({
  '&:nth-of-type(odd)': {
    backgroundColor: '#FFFFFF',
    border: '1px solid #EAECF0'
  },
  '&:nth-of-type(even)': {
    backgroundColor: '#F6F6F6',
    border: '1px solid #EAECF0'
  },
  '&:lastChild td, &:lastChild th': {
    border: 0
  }
}))

const statusColor = (status) => {
  if (status === 'resolved') return 'success'
  if (status === 'reprocessing') return 'warning'
  return 'error'
}

const formatDate = (d) => (d ? new Date(d).toLocaleString() : '—')

const DeadLetterList = ({
  data,
  page,
  rowsPerPage,
  count,
  handleChangePage,
  handleChangeRowsPerPage,
  onRetrigger,
  onResolve,
  loadingId
}) => {
  const [detailItem, setDetailItem] = useState(null)

  return (
    <div
      style={{
        overflowY: 'scroll',
        display: 'flex',
        maxHeight: `calc(100vh - 170px)`,
        width: '100%',
        scrollbarWidth: 'none',
        msOverflowStyle: 'none'
      }}
    >
      <TableContainer
        sx={{
          mt: 3,
          overflowY: 'auto',
          paddingInline: 0.4,
          maxHeight: '100%'
        }}
      >
        <Table stickyHeader aria-label="sticky table" sx={{ border: '1px solid #EAECF0' }}>
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Topic</StyledTableCell>
              <StyledTableCell>Group ID</StyledTableCell>
              <StyledTableCell>Error</StyledTableCell>
              <StyledTableCell>Fail Count</StyledTableCell>
              <StyledTableCell>Retrigger Count</StyledTableCell>
              <StyledTableCell>Status</StyledTableCell>
              <StyledTableCell>Last Failed</StyledTableCell>
              <StyledTableCell>Actions</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 && (
              <StyledTableRow>
                <StyledTableCell colSpan={8} sx={{ textAlign: 'center' }}>
                  No dead letter records found.
                </StyledTableCell>
              </StyledTableRow>
            )}
            {data.map((item, index) => (
              <StyledTableRow key={item._id || index}>
                <StyledTableCell>
                  <Typography fontFamily="poppins" fontSize={12} fontWeight={500} color="#333333">
                    {item.topic}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Typography fontFamily="poppins" fontSize={11} color="#667085">
                    {item.groupId}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Tooltip title={item.errorStack || item.error || ''} arrow>
                    <Typography
                      fontFamily="poppins"
                      fontSize={11}
                      color="error"
                      noWrap
                      sx={{ maxWidth: 240 }}
                    >
                      {item.error || '—'}
                    </Typography>
                  </Tooltip>
                </StyledTableCell>
                <StyledTableCell>
                  <Chip label={item.errorCount || 1} size="small" color={item.errorCount > 1 ? 'error' : 'default'} />
                </StyledTableCell>
                <StyledTableCell>{item.retriggerCount || 0}</StyledTableCell>
                <StyledTableCell>
                  <Chip label={item.status} size="small" color={statusColor(item.status)} />
                </StyledTableCell>
                <StyledTableCell>
                  <Typography fontFamily="poppins" fontSize={11}>
                    {formatDate(item.lastFailedAt)}
                  </Typography>
                </StyledTableCell>
                <StyledTableCell>
                  <Box display="flex" gap={0.5}>
                    <Tooltip title="View payload / error details" arrow>
                      <IconButton size="small" onClick={() => setDetailItem(item)}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {item.status !== 'resolved' && (
                      <>
                        <Tooltip title="Retrigger — republish to original topic" arrow>
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              disabled={loadingId === item._id || item.status === 'reprocessing'}
                              onClick={() => onRetrigger(item._id)}
                            >
                              {loadingId === item._id ? <CircularProgress size={16} /> : <RefreshIcon fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Mark as resolved" arrow>
                          <span>
                            <IconButton
                              size="small"
                              color="success"
                              disabled={loadingId === item._id}
                              onClick={() => onResolve(item._id)}
                            >
                              <CheckCircleOutlineIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </Box>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          backgroundColor: '#ffffff',
          padding: '2px',
          borderTop: '1px solid #dddddd'
        }}
        rowsPerPageOptions={[10, 15, 25, 45, 75, 100]}
        component="div"
        count={count || 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      <Dialog open={!!detailItem} onClose={() => setDetailItem(null)} maxWidth="md" fullWidth>
        <DialogTitle>Dead Letter Detail — {detailItem?.topic}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="subtitle2" gutterBottom>
            Offset: {detailItem?.partition} / {detailItem?.offset} &nbsp;|&nbsp; Key: {detailItem?.messageKey || '—'}{' '}
            &nbsp;|&nbsp; First failed: {formatDate(detailItem?.firstFailedAt)}
          </Typography>
          <Typography
            variant="subtitle2"
            color="error"
            gutterBottom
            sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}
          >
            Error: {detailItem?.error}
          </Typography>
          {detailItem?.errorStack && (
            <Box
              component="pre"
              sx={{
                fontSize: 11,
                backgroundColor: '#F6F6F6',
                border: '1px solid #EAECF0',
                borderRadius: 1,
                p: 1,
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                mb: 2
              }}
            >
              {detailItem.errorStack}
            </Box>
          )}
          <Typography variant="subtitle2" gutterBottom>
            Message Payload:
          </Typography>
          <Box
            component="pre"
            sx={{
              fontSize: 11,
              backgroundColor: '#F6F6F6',
              border: '1px solid #EAECF0',
              borderRadius: 1,
              p: 1,
              overflowX: 'auto',
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word'
            }}
          >
            {detailItem?.payload ? JSON.stringify(detailItem.payload, null, 2) : detailItem?.rawValue || '—'}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailItem(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default DeadLetterList
